import {abortS3MultipartUpload} from '@app/gen/files';
import {
  FileUpload,
  FileUploadState,
} from '@common/uploads/uploader/file-upload-store';
import {S3_MULTIPART_KEY_PREFIX} from '@common/uploads/uploader/strategy/s3-multipart-upload';
import {tusFingerprintFor} from '@common/uploads/uploader/strategy/tus-upload';
import {UploadStrategyConfig} from '@common/uploads/uploader/strategy/upload-strategy';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {getCookie} from '@ui/utils/hooks/use-cookie';

/**
 * Resume across reloads (build 128).
 *
 * Only metadata is stored (never file bytes): browsers do not let a page
 * re-open a file after a reload, so the user re-selects it and we match it by
 * name + size + last modified. The server keeps the bytes already received
 * (tus offset / S3 multipart parts) until the normal expiry cleans them up.
 */
const STORAGE_PREFIX = 'cd-uploads:';
// Server partial uploads expire (tus 24h after last activity, S3 cron after 24h).
export const INTERRUPTED_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const LEGACY_IDB_NAME = 'cloud-drive-uploads';

export type PersistedUpload = {
  id: string;
  name: string;
  size: number;
  mime: string;
  relativePath: string;
  lastModified: number;
  fingerprint: string;
  options: Pick<UploadStrategyConfig, 'uploadType' | 'metadata' | 'backendId'>;
  bytesUploaded: number;
  percentage: number;
  resumable: 'tus' | 's3-multipart' | 'restart';
  tusUrl?: string | null;
  s3?: {key: string; uploadId: string} | null;
  savedAt: number;
};

function storageKey(persistKey: string) {
  return `${STORAGE_PREFIX}${persistKey}`;
}

function readRaw(persistKey: string): PersistedUpload[] {
  try {
    const raw = localStorage.getItem(storageKey(persistKey));
    const rows = raw ? (JSON.parse(raw) as PersistedUpload[]) : [];
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

/** Upload URL tus-js-client stored for this file (key "tus::<fingerprint>::<n>"). */
export function findTusUrl(fileFingerprint: string): string | null {
  const prefix = `tus::${tusFingerprintFor(fileFingerprint)}::`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        const value = JSON.parse(localStorage.getItem(key) || '{}');
        if (value?.uploadUrl) return value.uploadUrl as string;
      }
    }
  } catch {
    //
  }
  return null;
}

export function findS3Multipart(
  fileFingerprint: string,
): {key: string; uploadId: string} | null {
  try {
    const raw = localStorage.getItem(`${S3_MULTIPART_KEY_PREFIX}${fileFingerprint}`);
    const value = raw ? JSON.parse(raw) : null;
    if (value?.fileKey && value?.uploadId) {
      return {key: value.fileKey, uploadId: value.uploadId};
    }
  } catch {
    //
  }
  return null;
}

function forgetLocalRefs(fileFingerprint: string) {
  const tusPrefix = `tus::${tusFingerprintFor(fileFingerprint)}::`;
  try {
    const remove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(tusPrefix)) remove.push(key);
    }
    remove.forEach(k => localStorage.removeItem(k));
    localStorage.removeItem(`${S3_MULTIPART_KEY_PREFIX}${fileFingerprint}`);
  } catch {
    //
  }
}

/**
 * Delete what the server holds for an upload that has no running request
 * (paused, failed or interrupted), then forget the local resume refs.
 */
export async function discardServerUpload(upload: {
  file: {fingerprint: string};
  options: Pick<UploadStrategyConfig, 'uploadType' | 'backendId'>;
}): Promise<void> {
  const fp = upload.file.fingerprint;
  const tusUrl = findTusUrl(fp);
  const s3 = findS3Multipart(fp);
  forgetLocalRefs(fp);
  const jobs: Promise<unknown>[] = [];
  if (tusUrl) {
    jobs.push(
      fetch(tusUrl, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          'Tus-Resumable': '1.0.0',
          'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') ?? '',
        },
      }).catch(() => null),
    );
  }
  if (s3 && upload.options.backendId) {
    jobs.push(
      abortS3MultipartUpload({
        key: s3.key,
        uploadId: s3.uploadId,
        uploadType: upload.options.uploadType,
        backendId: upload.options.backendId,
      }).catch(() => null),
    );
  }
  await Promise.all(jobs);
}

function toRecord(upload: FileUpload): PersistedUpload | null {
  const file = upload.file as UploadedFile;
  if (!file.native && upload.status !== 'interrupted') return null;
  if (upload.status === 'completed') return null;
  const tusUrl = findTusUrl(file.fingerprint);
  const s3 = findS3Multipart(file.fingerprint);
  return {
    id: file.id,
    name: file.name,
    size: file.size,
    mime: file.mime,
    relativePath: file.relativePath,
    lastModified: Number(file.lastModified) || 0,
    fingerprint: file.fingerprint,
    options: {
      uploadType: upload.options.uploadType,
      metadata: upload.options.metadata,
      backendId: upload.options.backendId,
    },
    bytesUploaded: upload.bytesUploaded,
    percentage: upload.percentage,
    resumable: tusUrl ? 'tus' : s3 ? 's3-multipart' : 'restart',
    tusUrl,
    s3,
    savedAt: upload.savedAt ?? Date.now(),
  };
}

export function persistActiveUploads(
  state: FileUploadState,
  persistKey: string,
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const rows: PersistedUpload[] = [];
    state.fileUploads.forEach(upload => {
      const record = toRecord(upload);
      if (record) rows.push(record);
    });
    if (rows.length) {
      localStorage.setItem(storageKey(persistKey), JSON.stringify(rows));
    } else {
      localStorage.removeItem(storageKey(persistKey));
    }
  } catch {
    // Quota or private mode: uploads still run in this tab.
  }
}

/** Unfinished uploads from a previous visit, shown as "Interrupted". */
export function restorePersistedUploads(persistKey: string): FileUpload[] {
  if (typeof localStorage === 'undefined') return [];
  dropLegacyBlobStore();
  const now = Date.now();
  const rows = readRaw(persistKey).filter(
    row => row?.id && row.name && now - (row.savedAt || 0) < INTERRUPTED_MAX_AGE_MS,
  );
  return rows.map(row => {
    // Placeholder without bytes; replaced when the user re-selects the file.
    const placeholder = new UploadedFile(
      new File([], row.name, {type: row.mime, lastModified: row.lastModified}),
      row.relativePath,
    );
    placeholder.id = row.id;
    placeholder.size = row.size;
    placeholder.lastModified = row.lastModified;
    placeholder.fingerprint = row.fingerprint;
    placeholder.relativePath = row.relativePath || '';
    if (row.mime) placeholder.mime = row.mime;
    return {
      file: placeholder,
      percentage: row.percentage || 0,
      bytesUploaded: row.bytesUploaded || 0,
      status: 'interrupted' as const,
      options: row.options,
      resumable: row.resumable,
      savedAt: row.savedAt,
    };
  });
}

export function clearPersistedUploads(persistKey: string): void {
  try {
    localStorage.removeItem(storageKey(persistKey));
  } catch {
    //
  }
}

/** A re-selected file is the same file when name, size and last-modified match. */
export function matchesInterrupted(
  file: File,
  upload: Pick<FileUpload, 'file'>,
): boolean {
  return (
    file.name === upload.file.name &&
    file.size === upload.file.size &&
    Number(file.lastModified) === Number(upload.file.lastModified)
  );
}

// Builds before 128 copied whole files into IndexedDB; free that space once.
let legacyDropped = false;
function dropLegacyBlobStore() {
  if (legacyDropped || typeof indexedDB === 'undefined') return;
  legacyDropped = true;
  try {
    indexedDB.deleteDatabase(LEGACY_IDB_NAME);
  } catch {
    //
  }
}

export function isResumableOnServer(upload: FileUpload): boolean {
  return upload.resumable === 'tus' || upload.resumable === 's3-multipart';
}

export function currentChunkSize(): number {
  return Number(getBootstrapData()?.settings?.uploading?.chunk_size) || 0;
}
